import { Grid, MenuItem, TextField } from '@mui/material'
import PropTypes from 'prop-types'
import { useState, useEffect } from 'react'
import { selectData } from 'layouts/onboarding'

const selectStyles = {
  '&>label': {
    fontSize: '14px',
    color: '#75787B',
    top: '-2px',
  },
  '&>label.Mui-focused': {
    fontSize: '1rem',
    color: '#2370ED',
    background: '#fff',
    padding: '0 5px',
    marginLeft: '-5px',
    top: 0,
  },
  '&>label.Mui-focused.Mui-error': {
    color: '#EA0606',
  },
  '&>label.MuiFormLabel-filled, &>label.MuiInputLabel-shrink': {
    fontSize: '1rem',
    background: '#fff',
    padding: '0 5px',
    marginLeft: '-5px',
    top: 0,
  },
  '&>label.MuiFormLabel-filled.Mui-disabled': {
    top: 0,
    background:
      'linear-gradient(to bottom, #fff 0%, #fff 50%, #E9ECEF 50%, #E9ECEF 100%)',
  },
  '&>div.MuiInputBase-root': {
    padding: '0!important',
    borderRadius: '4px !important',
  },
  '&>div.MuiInputBase-root.Mui-disabled': {
    backgroundColor: '#E9ECEF!important',
  },
  '&>div.MuiInputBase-root.Mui-focused': {
    boxShadow: '0 0 0 1.5px #2370ED',
  },
  '&>div>.MuiInputBase-input': {
    width: '100%!important',
    padding: '0.75rem 1.75rem 0.75rem 0.75rem!important',
    display: 'flex',
  },
  '&>div>.MuiInputBase-input>.MuiListItemIcon-root': {
    minWidth: 'unset',
  },
  '&>div>.MuiInputBase-input>.MuiListItemIcon-root>img': {
    height: '20px',
    width: 'auto',
    marginRight: '8px',
  },
  '&>div>.MuiInputBase-input>.MuiListItemText-root>span': {
    fontSize: '14px',
  },
  '&>div.MuiInputBase-root>svg': {
    display: 'block',
    height: '1.5em',
    width: '1.5em',
    top: 'calc(50% - 0.75em)',
  },
}

export const MuiSplitDateField = ({ value = '', onChange, label }) => {
  // Parse initial value if it’s a valid yyyy-MM-dd
  const parseValue = (v) => {
    if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) {
      const [y, m, d] = v.split('-')
      return { year: y, month: m, day: d }
    }
    return { year: '', month: '', day: '' }
  }

  const [internal, setInternal] = useState(parseValue(value))

  // Keep internal state in sync if parent resets value
  useEffect(() => {
    if (!value) {
      setInternal({ year: '', month: '', day: '' })
    } else if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      setInternal(parseValue(value))
    }
  }, [value])

  const getDaysInMonth = (y, m) => {
    if (!y || !m) return 31
    return new Date(Number(y), Number(m), 0).getDate()
  }

  const handleUpdate = (field, newVal) => {
    const updated = { ...internal, [field]: newVal }
    setInternal(updated)

    const { year, month, day } = updated
    if (year && month && day) {
      const validDayCount = getDaysInMonth(year, month)
      const adjustedDay = Math.min(Number(day), validDayCount)
        .toString()
        .padStart(2, '0')

      const formatted = `${year}-${month}-${adjustedDay}`
      onChange?.(formatted)
    } else {
      // Not complete yet → treat as empty
      onChange?.('')
    }
  }

  return (
    <Grid container spacing={2}>
      {label && (
        <Grid item xs={12}>
          {label}
        </Grid>
      )}

      {/* Month */}
      <Grid item xs={4}>
        <TextField
          select
          fullWidth
          label="Month"
          value={internal.month}
          onChange={(e) =>
            handleUpdate('month', e.target.value.padStart(2, '0'))
          }
          sx={selectStyles}
          SelectProps={{
            MenuProps: {
              PaperProps: {
                style: {
                  maxHeight: 300,
                },
              },
              MenuListProps: {
                autoFocusItem: true,
              },
            },
          }}
        >
          {selectData.datesMonth.map(({ value, label }) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      {/* Day */}
      <Grid item xs={4}>
        <TextField
          select
          fullWidth
          label="Day"
          value={internal.day}
          onChange={(e) => handleUpdate('day', e.target.value.padStart(2, '0'))}
          sx={selectStyles}
          SelectProps={{
            MenuProps: {
              PaperProps: {
                style: {
                  maxHeight: 300,
                },
              },
              MenuListProps: {
                autoFocusItem: true,
              },
            },
          }}
        >
          {Array.from(
            { length: getDaysInMonth(internal.year, internal.month) },
            (_, i) => {
              const dayValue = String(i + 1).padStart(2, '0')
              return (
                <MenuItem key={dayValue} value={dayValue}>
                  {dayValue}
                </MenuItem>
              )
            }
          )}
        </TextField>
      </Grid>

      {/* Year */}
      <Grid item xs={4}>
        <TextField
          select
          fullWidth
          label="Year"
          value={internal.year}
          onChange={(e) => handleUpdate('year', e.target.value)}
          sx={selectStyles}
          SelectProps={{
            MenuProps: {
              PaperProps: {
                style: {
                  maxHeight: 300,
                },
              },
              MenuListProps: {
                autoFocusItem: true,
              },
            },
          }}
        >
          {selectData.datesYear.map(({ value, label }) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
    </Grid>
  )
}

MuiSplitDateField.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  label: PropTypes.string,
}
